import { useCallback, useEffect, useRef, useState } from "react";
import useUnsavedChanges from "./useUnsavedChanges";

const useFormDirty = (form) => {
  const [isDirty, setIsDirty] = useState(false);
  const initialValueRef = useRef(null);
  const unsavedChane = useUnsavedChanges(isDirty);

  // Top-level fields the user typed in since the last setInitialValue, with
  // the value each one had just before the user's first edit
  const userEditedRef = useRef(new Map());

  /**
   * Record the "saved" values. Stable (refs + state setter), safe in effect
   * dependencies.
   *
   * `keepUserEdits`: for the baseline a page records a tick after loading
   * (setTimeout, so fields can settle). If the user already typed in a field
   * by then, its saved value is the one it had before the user's first edit
   * (normally the loaded value), so the edit still counts as unsaved and
   * typing the loaded value back makes it clean again. Without it (e.g.
   * after a save) everything is saved.
   */
  const setInitialValue = useCallback((value, { keepUserEdits = false } = {}) => {
    // react-hook-form hands out its live values object, so keep a snapshot
    const next = snapshot(value);
    if (keepUserEdits && next && typeof next === "object") {
      userEditedRef.current.forEach((before, key) => {
        if (key in next) next[key] = before;
      });
    } else {
      userEditedRef.current.clear();
    }
    initialValueRef.current = next;
    setIsDirty(!!form && keepUserEdits && !sameValues(snapshot(form.getValues()), next));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the form instance is created once
  }, []);

  // Remember which fields the user typed in (DOM input events; values set or
  // normalised by code with setValue fire none, so they don't count). This
  // capture listener runs before React's onChange, so the form still holds
  // the value from before the edit.
  useEffect(() => {
    if (!form) return;
    const onInput = (event) => {
      const name = event.target?.name;
      if (!name) return;
      const key = name.split(".")[0];
      const values = form.getValues();
      if (values && key in values && !userEditedRef.current.has(key)) {
        userEditedRef.current.set(key, snapshot(values[key]));
      }
    };
    document.addEventListener("input", onInput, true);
    return () => document.removeEventListener("input", onInput, true);
  }, [form]);

  /**
   * Update only some keys of the saved values (e.g. select options that
   * arrive after the form opened). Unlike setInitialValue(form.getValues()),
   * an edit the user already made in another field stays unsaved.
   */
  const mergeInitialValue = useCallback((partial) => {
    const next = { ...(initialValueRef.current ?? {}), ...snapshot(partial) };
    initialValueRef.current = next;
    if (form) setIsDirty(!sameValues(snapshot(form.getValues()), next));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- the form instance is created once
  }, []);

  useEffect(() => {
    if (!form) return;

    // Until the page records its starting values (setInitialValue, often a
    // tick later, after loading), compare with the values the form opened
    // with: a change typed in that moment still counts as unsaved
    if (initialValueRef.current === null) {
      initialValueRef.current = snapshot(form.getValues());
    }

    const subscription = form.watch((value) => {
      if (initialValueRef.current === null) {
        return;
      }

      const isSame = sameValues(value, initialValueRef.current);
      const dirty = !isSame;

      setIsDirty(dirty);
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [form]);

  return {
    isDirty,
    setInitialValue,
    mergeInitialValue,
    ...unsavedChane
  };
};

export default useFormDirty;

/**
 * Deep equality where a number and the same number typed as text count as
 * equal: loaded values are numbers (40), an input gives text ("40"), so
 * retyping a loaded number must not leave the form "changed".
 */
export const sameValues = (a, b) => {
  if (a === b) return true;
  if (typeof a === "number" || typeof b === "number") {
    return (typeof a === "number" || typeof a === "string") &&
      (typeof b === "number" || typeof b === "string") &&
      String(a).trim() !== "" && String(b).trim() !== "" &&
      Number(a) === Number(b);
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    return Array.isArray(a) && Array.isArray(b) && a.length === b.length &&
      a.every((x, i) => sameValues(x, b[i]));
  }
  const plain = (v) => v && Object.getPrototypeOf(v) === Object.prototype;
  if (plain(a) && plain(b)) {
    const keys = Object.keys(a);
    return keys.length === Object.keys(b).length &&
      keys.every((k) => Object.prototype.hasOwnProperty.call(b, k) && sameValues(a[k], b[k]));
  }
  // Dates, Files...: by value where it makes sense
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  return false;
};

// Deep copy of plain objects/arrays; other values (File, Date, ...) by reference
const snapshot = (v) => {
  if (Array.isArray(v)) return v.map(snapshot);
  if (v && Object.getPrototypeOf(v) === Object.prototype) {
    return Object.fromEntries(Object.entries(v).map(([k, x]) => [k, snapshot(x)]));
  }
  return v;
};

